import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fmtUsd } from "@/lib/format";
import { Star, DollarSign, Receipt, CheckCircle2, Banknote } from "lucide-react";

/**
 * v2.6.9 batch BB · Cash Register · Trip Close-Out
 *
 * Opens at end of trip in City Taxi, Custom Trip, and Live Meter. Mimics a
 * digital cash register: shows total due, lets the driver punch in cash
 * tendered + tip, auto-calcs change due, then captures a 1-5 star rating.
 *
 * Submit returns the full closeout payload to the caller:
 *   { payment_method, tip_amount, cash_tendered, change_due, rating, rating_comment, total_charged }
 *
 * Props:
 *   open · onOpenChange · subtotal · cc_surcharge · total (fare total, before tip)
 *   defaultPaymentMethod ("cash" | "card") · cardCollected (boolean — was card already pre-charged)
 *   onConfirm(payload) — called when driver hits CLOSE OUT TRIP. Caller is responsible
 *     for persisting the trip and closing the dialog after the network round-trip.
 *   busy (boolean) — disables submit while parent saves the trip.
 */
export default function TripCloseoutDialog({
  open,
  onOpenChange,
  subtotal = 0,
  cc_surcharge = 0,
  total = 0,
  defaultPaymentMethod = "cash",
  cardCollected = false,
  onConfirm,
  busy = false,
}) {
  const [payment, setPayment] = useState(defaultPaymentMethod);
  const [tipInput, setTipInput] = useState("");
  const [cashInput, setCashInput] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  // Reset all state every time the dialog opens fresh
  useEffect(() => {
    if (open) {
      setPayment(defaultPaymentMethod);
      setTipInput("");
      setCashInput("");
      setRating(0);
      setComment("");
    }
  }, [open, defaultPaymentMethod]);

  const tip = parseFloat(tipInput) || 0;
  const grandTotal = useMemo(() => Math.max(0, Number(total) + tip), [total, tip]);
  const cashTendered = parseFloat(cashInput) || 0;
  const change = useMemo(() => +(cashTendered - grandTotal).toFixed(2), [cashTendered, grandTotal]);

  const applyTipPct = (pct) => {
    // Tip percent applies on the pre-surcharge subtotal so the rider isn't tipped on the CC fee.
    const base = Number(subtotal) > 0 ? Number(subtotal) : Number(total) || 0;
    const t = (base * pct) / 100;
    setTipInput(t.toFixed(2));
  };

  const handleConfirm = () => {
    if (!rating) return;
    if (payment === "cash" && cashTendered < grandTotal) return;
    onConfirm?.({
      payment_method: payment,
      tip_amount: +tip.toFixed(2),
      cash_tendered: payment === "cash" ? +cashTendered.toFixed(2) : null,
      change_due: payment === "cash" ? change : null,
      rating,
      rating_comment: comment.trim() || null,
      total_charged: +grandTotal.toFixed(2),
    });
  };

  const canSubmit = rating > 0 && (payment !== "cash" || cashTendered >= grandTotal) && !busy;
  const cashShort = payment === "cash" && cashInput && cashTendered < grandTotal;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="trip-closeout-dialog"
        className="bg-zinc-950 border-amber-400/40 text-zinc-100 max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Receipt className="h-5 w-5" />
            Cash Register · Close Out Trip
          </DialogTitle>
        </DialogHeader>

        {/* ============================ REGISTER DISPLAY ============================ */}
        <div className="bg-zinc-900 border border-amber-400/30 rounded-lg p-4 shadow-inner">
          <div className="flex justify-between text-xs text-zinc-500 uppercase tracking-widest">
            <span>Subtotal</span>
            <span data-testid="closeout-subtotal" className="meter-display text-zinc-300">{fmtUsd(subtotal)}</span>
          </div>
          {cc_surcharge > 0 && (
            <div className="flex justify-between text-xs text-zinc-500 uppercase tracking-widest mt-1">
              <span>CC Processing</span>
              <span data-testid="closeout-cc" className="meter-display text-zinc-300">{fmtUsd(cc_surcharge)}</span>
            </div>
          )}
          <div className="flex justify-between mt-2 pt-2 border-t border-zinc-800">
            <span className="text-xs text-zinc-400 uppercase tracking-widest">Fare</span>
            <span data-testid="closeout-fare" className="meter-display text-lg font-bold text-zinc-100">{fmtUsd(total)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-zinc-400 uppercase tracking-widest">Tip</span>
            <span data-testid="closeout-tip" className="meter-display text-lg font-bold text-emerald-400">{fmtUsd(tip)}</span>
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t-2 border-amber-400/40">
            <span className="text-xs text-amber-400 uppercase tracking-widest font-bold">Total Due</span>
            <span data-testid="closeout-grand-total" className="meter-display text-3xl font-extrabold text-amber-400">{fmtUsd(grandTotal)}</span>
          </div>
        </div>

        {/* ============================ PAYMENT METHOD ============================ */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Payment Method</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="closeout-pay-cash"
              onClick={() => setPayment("cash")}
              className={`h-11 rounded-md border-2 font-bold uppercase text-xs tracking-widest transition-all ${
                payment === "cash" ? "bg-green-500/15 border-green-400 text-green-400" : "bg-zinc-900 border-zinc-700 text-zinc-400"
              }`}
            >
              <Banknote className="h-4 w-4 inline mr-1.5 -mt-0.5" />Cash
            </button>
            <button
              type="button"
              data-testid="closeout-pay-card"
              onClick={() => setPayment("card")}
              className={`h-11 rounded-md border-2 font-bold uppercase text-xs tracking-widest transition-all ${
                payment === "card" ? "bg-amber-400/15 border-amber-400 text-amber-400" : "bg-zinc-900 border-zinc-700 text-zinc-400"
              }`}
            >
              <DollarSign className="h-4 w-4 inline mr-1 -mt-0.5" />Card
            </button>
          </div>
          {payment === "card" && cardCollected && (
            <div className="flex items-center gap-2 text-[11px] text-green-300 bg-green-500/10 border border-green-400/40 rounded px-2 py-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />Card was pre-charged before the trip · tip will be added separately.
            </div>
          )}
        </div>

        {/* ============================ TIP ENTRY ============================ */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Tip Amount</Label>
          <div className="grid grid-cols-5 gap-1.5 mb-1">
            {[0, 15, 18, 20, 25].map((p) => (
              <button
                key={p}
                type="button"
                data-testid={`closeout-tip-${p}`}
                onClick={() => (p === 0 ? setTipInput("") : applyTipPct(p))}
                className="h-9 rounded-md text-xs font-semibold bg-zinc-900 border border-zinc-700 hover:border-amber-400/60 hover:text-amber-400"
              >
                {p === 0 ? "None" : `${p}%`}
              </button>
            ))}
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400 font-bold">$</span>
            <Input
              data-testid="closeout-tip-input"
              type="number"
              step="0.01"
              min="0"
              value={tipInput}
              onChange={(e) => setTipInput(e.target.value)}
              placeholder="0.00"
              className="bg-zinc-900 border-zinc-700 h-11 pl-7 meter-display text-lg"
            />
          </div>
        </div>

        {/* ============================ CASH REGISTER ENTRY ============================ */}
        {payment === "cash" && (
          <div className="space-y-2 bg-green-500/5 border border-green-400/30 rounded-lg p-3" data-testid="closeout-cash-section">
            <Label className="text-[10px] uppercase tracking-wider text-green-400 font-bold">Cash Tendered</Label>
            <div className="grid grid-cols-4 gap-1.5 mb-1">
              {[5, 10, 20, 50].map((d) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`closeout-cash-${d}`}
                  onClick={() => setCashInput(((parseFloat(cashInput) || 0) + d).toFixed(2))}
                  className="h-9 rounded-md text-xs font-bold bg-zinc-900 border border-green-400/30 text-green-400 hover:bg-green-500/10"
                >
                  +${d}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <button
                type="button"
                data-testid="closeout-cash-exact"
                onClick={() => setCashInput(grandTotal.toFixed(2))}
                className="h-9 rounded-md text-xs font-bold bg-zinc-900 border border-green-400/30 text-green-400 hover:bg-green-500/10"
              >
                Exact
              </button>
              <button
                type="button"
                data-testid="closeout-cash-100"
                onClick={() => setCashInput(((parseFloat(cashInput) || 0) + 100).toFixed(2))}
                className="h-9 rounded-md text-xs font-bold bg-zinc-900 border border-green-400/30 text-green-400 hover:bg-green-500/10"
              >
                +$100
              </button>
              <button
                type="button"
                data-testid="closeout-cash-clear"
                onClick={() => setCashInput("")}
                className="h-9 rounded-md text-xs font-bold bg-zinc-900 border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
              >
                Clear
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-400 font-bold">$</span>
              <Input
                data-testid="closeout-cash-input"
                type="number"
                step="0.01"
                min="0"
                value={cashInput}
                onChange={(e) => setCashInput(e.target.value)}
                placeholder="0.00"
                className="bg-zinc-900 border-green-400/40 h-11 pl-7 meter-display text-lg"
              />
            </div>

            <div className={`flex justify-between items-center mt-2 pt-2 border-t ${cashShort ? "border-red-400/60" : "border-green-400/40"}`}>
              <span className={`text-xs uppercase tracking-widest font-bold ${cashShort ? "text-red-400" : "text-green-400"}`}>
                {cashShort ? "Short by" : "Change Due"}
              </span>
              <span
                data-testid="closeout-change-due"
                className={`meter-display text-2xl font-extrabold ${cashShort ? "text-red-400" : "text-green-400"}`}
              >
                {fmtUsd(Math.abs(change))}
              </span>
            </div>
          </div>
        )}

        {/* ============================ STAR RATING ============================ */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Rate This Trip · 1–5 Stars *</Label>
          <div className="flex items-center justify-center gap-2 py-1" data-testid="closeout-rating">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`closeout-rating-${s}`}
                onClick={() => setRating(s)}
                className={`h-12 w-12 rounded-md border-2 flex items-center justify-center transition-all ${
                  s <= rating
                    ? "bg-amber-400/20 border-amber-400 text-amber-400 scale-105"
                    : "bg-zinc-900 border-zinc-700 text-zinc-600 hover:border-amber-400/40"
                }`}
              >
                <Star className={`h-6 w-6 ${s <= rating ? "fill-amber-400" : ""}`} />
              </button>
            ))}
          </div>
          <Textarea
            data-testid="closeout-rating-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment (great rider, late, etc.)"
            rows={2}
            className="bg-zinc-900 border-zinc-700 text-sm"
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-1">
          <Button
            data-testid="closeout-cancel"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            data-testid="closeout-confirm"
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-12 px-6"
          >
            {busy ? "Saving…" : "CLOSE OUT TRIP"}
          </Button>
        </DialogFooter>
        {!rating && (
          <p className="text-[11px] text-amber-400/80 text-center -mt-2">Pick a 1–5 star rating to close out the trip.</p>
        )}
        {cashShort && (
          <p className="text-[11px] text-red-400 text-center -mt-2">Cash tendered is less than the total due.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
